import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import Input from '@shared/Input';
import Button from '@shared/Button';
import { toast } from 'react-hot-toast';
import { Mail } from 'lucide-react'; // For the email icon

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setMessage('If an account with that email exists, a password reset link has been sent.');
      toast.success('Password reset email sent (if email exists).');
      // Optionally close the modal after a short delay
      setTimeout(onClose, 3000);
    } catch (error: any) {
      setMessage(error.message || 'Failed to send password reset email. Please try again.');
      toast.error(error.message || 'Failed to send password reset email.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600/75 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Forgot Password</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            &times;
          </button>
        </div>
        {message ? (
          <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-4 mb-6" role="alert">
            <p className="flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              {message}
            </p>
          </div>
        ) : (
          <p className="text-gray-600 mb-6">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || !!message}
            />
          </div>
          <Button type="submit" loading={loading} disabled={loading || !!message} className="w-full">
            Send Reset Link
          </Button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/login" onClick={onClose} className="text-primary-500 hover:underline text-sm">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
